import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus, Minus, Edit2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import SwipeToExecute from '../components/SwipeToExecute';
import OrderConfirmationModal from '../components/OrderConfirmationModal';
import TradeJournalModal from '../components/TradeJournalModal';
import SentimentStrip from '../components/SentimentStrip';
import { formatPrice } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ORDER_TYPES = ['Regular', 'MTF', 'Iceberg', 'Cover'];
const VALIDITY_OPTIONS = ['DAY', 'IOC'];

function AdvancedTradePage() {
  const { user } = useAuth();
  const { colors } = useTheme();
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOrderWindow, setShowOrderWindow] = useState(false);
  
  // Order state
  const [orderType, setOrderType] = useState('BUY');
  const [selectedOrderTab, setSelectedOrderTab] = useState('Regular');
  const [exchange, setExchange] = useState('NSE');
  const [quantity, setQuantity] = useState(1);
  const [limitPrice, setLimitPrice] = useState('');
  const [duration, setDuration] = useState('Intraday');
  const [enableStoploss, setEnableStoploss] = useState(false);
  const [stoploss, setStoploss] = useState({ trigger: '', limit: '' });
  const [marketProtection, setMarketProtection] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [validity, setValidity] = useState('DAY');
  const [disclosedQty, setDisclosedQty] = useState('');
  
  // Modals
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  useEffect(() => {
    fetchStocks();
  }, []);

  useEffect(() => {
    if (selectedStock) {
      setLimitPrice(selectedStock.current_price.toString());
      setStoploss({
        trigger: (selectedStock.current_price * 0.98).toFixed(2),
        limit: (selectedStock.current_price * 0.98).toFixed(2),
      });
    }
  }, [selectedStock]);

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API_URL}/stocks`);
      setStocks(response.data);
    } catch (error) {
      console.error('Failed to fetch stocks', error);
    }
  };

  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
    setShowOrderWindow(true);
    setSearchTerm('');
  };

  const handleSwipeExecute = () => {
    const orderDetails = {
      symbol: selectedStock.symbol,
      order_type: orderType,
      quantity,
      price: parseFloat(limitPrice),
      duration,
      stoploss: enableStoploss ? parseFloat(stoploss.trigger) : null,
      target: null,
    };
    setPendingOrder(orderDetails);
    setShowConfirmation(true);
  };

  const handleConfirmOrder = () => {
    setShowConfirmation(false);
    setShowJournal(true);
  };

  const handleJournalSubmit = async (journalData) => {
    try {
      const orderPayload = {
        ...pendingOrder,
        ...journalData,
        order_variant: 'MARKET',
      };

      const response = await axios.post(`${API_URL}/trade/order`, orderPayload);
      
      toast.success(
        <div>
          <p className="font-semibold">Order Placed Successfully!</p>
          <p className="text-sm mt-1">{response.data.message}</p>
        </div>,
        { duration: 5000 }
      );

      // Reset form
      setShowOrderWindow(false);
      setSelectedStock(null);
      setQuantity(1);
      setEnableStoploss(false);
      setPendingOrder(null);
    } catch (error) {
      toast.error('Failed to place order');
    }
  };

  const filteredStocks = stocks.filter(stock =>
    stock.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const orderValue = selectedStock ? selectedStock.current_price * quantity : 0;
  const marginRequired = orderValue * (duration === 'Intraday' ? 0.2 : 1);
  const charges = orderValue * 0.0003;
  const canAfford = user && orderType === 'BUY' ? marginRequired <= user.virtual_balance : true;

  if (!showOrderWindow) {
    // Stock search view
    return (
      <div className="space-y-4" data-testid="advanced-trade-page" style={{ paddingBottom: '100px' }}>
        <div>
          <h1 className="text-[17px] font-bold mb-1" style={{ color: colors.primaryText }}>
            Trade
          </h1>
          <p style={{ color: colors.secondaryText }}>
            Professional order placement with advanced features
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: colors.secondaryText }} />
          <input
            type="text"
            placeholder="Search stocks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-2.5 rounded-lg text-[14px]"
            style={{
              background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
              borderWidth: '1px',
              borderColor: colors.border,
              color: colors.primaryText,
            }}
          />
        </div>

        {/* Stock List */}
        {searchTerm && (
          <div
            className="app-card rounded-xl overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
              borderWidth: '1px',
              borderColor: colors.border,
            }}
          >
            {filteredStocks.slice(0, 8).map((stock) => (
              <button
                key={stock.symbol}
                onClick={() => handleStockSelect(stock)}
                className="w-full px-3 py-2.5 text-left hover:opacity-80 transition-opacity"
                style={{ borderBottom: `1px solid ${colors.border}` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[14px]" style={{ color: colors.primaryText }}>
                      {stock.symbol.replace('.NS', '')}
                    </p>
                    <p className="text-sm" style={{ color: colors.secondaryText }}>
                      {stock.company_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: colors.primaryText }}>
                      {formatPrice(stock.current_price)}
                    </p>
                    <p
                      className="text-sm font-semibold"
                      style={{
                        color: stock.change_pct >= 0 ? colors.profit : colors.loss,
                      }}
                    >
                      {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Info card */}
        {!searchTerm && (
          <div
            className="app-card p-4 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
              borderWidth: '1px',
              borderColor: colors.border,
            }}
          >
            <h3 className="font-semibold mb-4" style={{ color: colors.primaryText }}>
              Professional Trading Features
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <span style={{ color: colors.accent }}>•</span>
                <span style={{ color: colors.secondaryText }}>
                  Swipe to execute - prevents accidental trades
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span style={{ color: colors.accent }}>•</span>
                <span style={{ color: colors.secondaryText }}>
                  Mandatory trade journal with strategy tags
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span style={{ color: colors.accent }}>•</span>
                <span style={{ color: colors.secondaryText }}>
                  Pre-trade checklist for better discipline
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span style={{ color: colors.accent }}>•</span>
                <span style={{ color: colors.secondaryText }}>
                  Advanced order types and stop-loss management
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>
    );
  }

  // Order window (continues in next part due to size...)
  const isBuy = orderType === 'BUY';
  const accentColor = isBuy ? '#6366F1' : '#EF4444';

  return (
    <>
      <div 
        className="fixed inset-0 z-40 overflow-y-auto"
        style={{ backgroundColor: colors.background, paddingBottom: '160px' }}
      >
        {/* Header */}
        <div
          className="app-card sticky top-0 z-10 px-4 py-3 flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <button
            onClick={() => setShowOrderWindow(false)}
            className="text-lg font-semibold"
            style={{ color: colors.accent }}
          >
            ← Back
          </button>
          <h2 className="text-lg font-bold" style={{ color: colors.primaryText }}>
            {selectedStock?.symbol.replace('.NS', '')}
          </h2>
          <div className="w-16"></div>
        </div>

        {/* Exchange Selection */}
        <div className="px-4 py-3 flex items-center space-x-4">
          <button
            onClick={() => setExchange('NSE')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: exchange === 'NSE' ? `${accentColor}15` : colors.inputBg,
              borderWidth: '1px',
              borderColor: exchange === 'NSE' ? accentColor : colors.border,
            }}
          >
            <div
              className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              style={{ borderColor: exchange === 'NSE' ? accentColor : colors.border }}
            >
              {exchange === 'NSE' && (
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: accentColor }}></div>
              )}
            </div>
            <span style={{ color: colors.primaryText }}>
              NSE {formatPrice(selectedStock?.current_price)}
            </span>
          </button>

          <button
            onClick={() => setExchange('BSE')}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: exchange === 'BSE' ? `${accentColor}15` : colors.inputBg,
              borderWidth: '1px',
              borderColor: exchange === 'BSE' ? accentColor : colors.border,
            }}
          >
            <div
              className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              style={{ borderColor: exchange === 'BSE' ? accentColor : colors.border }}
            >
              {exchange === 'BSE' && (
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: accentColor }}></div>
              )}
            </div>
            <span style={{ color: colors.primaryText }}>
              BSE {formatPrice(selectedStock?.current_price * 0.999)}
            </span>
          </button>
        </div>

        {/* BUY/SELL Toggle */}
        <div className="px-4 py-2 flex space-x-2">
          <button
            onClick={() => setOrderType('BUY')}
            className="flex-1 py-2.5 rounded-lg font-semibold"
            style={{
              backgroundColor: orderType === 'BUY' ? '#6366F1' : colors.inputBg,
              color: orderType === 'BUY' ? '#ffffff' : colors.primaryText,
            }}
          >
            BUY
          </button>
          <button
            onClick={() => setOrderType('SELL')}
            className="flex-1 py-2.5 rounded-lg font-semibold"
            style={{
              backgroundColor: orderType === 'SELL' ? '#EF4444' : colors.inputBg,
              color: orderType === 'SELL' ? '#ffffff' : colors.primaryText,
            }}
          >
            SELL
          </button>
        </div>

        {/* Order Type Tabs */}
        <div className="px-4 py-2 flex space-x-1 overflow-x-auto">
          {ORDER_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedOrderTab(type)}
              className="px-4 py-2 whitespace-nowrap"
              style={{
                color: selectedOrderTab === type ? accentColor : colors.secondaryText,
                borderBottom: selectedOrderTab === type ? `3px solid ${accentColor}` : 'none',
                fontWeight: selectedOrderTab === type ? '600' : '400',
              }}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="px-4 space-y-4">
          {/* Quantity */}
          <div>
            <label className="block text-sm mb-2" style={{ color: colors.secondaryText }}>
              Quantity
            </label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-3 rounded-lg"
                style={{ backgroundColor: colors.inputBg }}
              >
                <Minus className="w-5 h-5" style={{ color: colors.primaryText }} />
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="flex-1 px-4 py-2.5 rounded-lg text-center text-[14px] font-semibold"
                style={{
                  backgroundColor: colors.inputBg,
                  borderWidth: '1px',
                  borderColor: colors.border,
                  color: colors.primaryText,
                }}
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-3 rounded-lg"
                style={{ backgroundColor: colors.inputBg }}
              >
                <Plus className="w-5 h-5" style={{ color: colors.primaryText }} />
              </button>
            </div>
          </div>

          {/* Limit Price */}
          <div>
            <label className="block text-sm mb-2" style={{ color: colors.secondaryText }}>
              Limit Price
            </label>
            <div className="relative">
              <input
                type="number"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                className="w-full px-4 py-2.5 pr-12 rounded-lg text-[14px] font-semibold"
                style={{
                  backgroundColor: colors.inputBg,
                  borderWidth: '1px',
                  borderColor: colors.border,
                  color: colors.primaryText,
                }}
              />
              <Edit2 className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: colors.secondaryText }} />
            </div>
          </div>

          {/* Duration */}
          <div className="flex space-x-3">
            <button
              onClick={() => setDuration('Intraday')}
              className="flex-1 py-2.5 rounded-lg font-medium"
              style={{
                backgroundColor: duration === 'Intraday' ? `${accentColor}15` : colors.inputBg,
                color: duration === 'Intraday' ? accentColor : colors.primaryText,
                borderWidth: '1px',
                borderColor: duration === 'Intraday' ? accentColor : colors.border,
              }}
            >
              Intraday
            </button>
            <button
              onClick={() => setDuration('Longterm')}
              className="flex-1 py-2.5 rounded-lg font-medium"
              style={{
                backgroundColor: duration === 'Longterm' ? `${accentColor}15` : colors.inputBg,
                color: duration === 'Longterm' ? accentColor : colors.primaryText,
                borderWidth: '1px',
                borderColor: duration === 'Longterm' ? accentColor : colors.border,
              }}
            >
              Long Term
            </button>
          </div>

          {/* Stoploss */}
          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: colors.inputBg,
              borderWidth: '1px',
              borderColor: colors.border,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="font-medium" style={{ color: colors.primaryText }}>
                  Stoploss
                </span>
                <Info className="w-4 h-4" style={{ color: colors.secondaryText }} />
              </div>
              <button
                onClick={() => setEnableStoploss(!enableStoploss)}
                className="relative w-12 h-6 rounded-full transition-colors"
                style={{
                  backgroundColor: enableStoploss ? accentColor : colors.border,
                }}
              >
                <div
                  className="absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform"
                  style={{
                    left: enableStoploss ? 'calc(100% - 22px)' : '2px',
                  }}
                ></div>
              </button>
            </div>

            {enableStoploss && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: colors.secondaryText }}>
                    Trigger Price
                  </label>
                  <input
                    type="number"
                    value={stoploss.trigger}
                    onChange={(e) => setStoploss({ ...stoploss, trigger: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg"
                    style={{
                      backgroundColor: colors.background,
                      borderWidth: '1px',
                      borderColor: colors.border,
                      color: colors.primaryText,
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: colors.secondaryText }}>
                    Limit
                  </label>
                  <input
                    type="number"
                    value={stoploss.limit}
                    onChange={(e) => setStoploss({ ...stoploss, limit: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg"
                    style={{
                      backgroundColor: colors.background,
                      borderWidth: '1px',
                      borderColor: colors.border,
                      color: colors.primaryText,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Market Protection */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="font-medium" style={{ color: colors.primaryText }}>
                Market Protection
              </span>
              <Info className="w-4 h-4" style={{ color: colors.secondaryText }} />
            </div>
            <button
              onClick={() => setMarketProtection(!marketProtection)}
              className="relative w-12 h-6 rounded-full transition-colors"
              style={{
                backgroundColor: marketProtection ? accentColor : colors.border,
              }}
            >
              <div
                className="absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform"
                style={{
                  left: marketProtection ? 'calc(100% - 22px)' : '2px',
                }}
              ></div>
            </button>
          </div>

          {/* Advanced Options */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-3"
            style={{ color: colors.accent }}
          >
            <span className="font-medium">Advanced</span>
            {showAdvanced ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {showAdvanced && (
            <div className="space-y-4 pb-4">
              <div>
                <label className="block text-sm mb-2" style={{ color: colors.secondaryText }}>
                  Validity
                </label>
                <div className="flex space-x-2">
                  {VALIDITY_OPTIONS.map((option) => (
                    <button
                      key={option}
                      onClick={() => setValidity(option)}
                      className="px-4 py-2 rounded-lg"
                      style={{
                        backgroundColor: validity === option ? accentColor : colors.inputBg,
                        color: validity === option ? '#ffffff' : colors.primaryText,
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2" style={{ color: colors.secondaryText }}>
                  Disclosed Quantity
                </label>
                <input
                  type="number"
                  value={disclosedQty}
                  onChange={(e) => setDisclosedQty(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    backgroundColor: colors.inputBg,
                    borderWidth: '1px',
                    borderColor: colors.border,
                    color: colors.primaryText,
                  }}
                />
              </div>
            </div>
          )}

          {/* Edge Score */}
          {selectedStock?.symbol && (
            <SentimentStrip
              symbol={selectedStock.symbol}
              colors={colors}
            />
          )}

          {/* Margin Info */}
          <div
            className="p-3 rounded-lg"
            style={{
              backgroundColor: colors.inputBg,
              borderWidth: '1px',
              borderColor: colors.border,
            }}
          >
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: colors.secondaryText }}>Margin Required</span>
              <span className="font-semibold" style={{ color: colors.primaryText }}>
                {formatPrice(marginRequired)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span style={{ color: colors.secondaryText }}>Charges</span>
              <span className="font-semibold" style={{ color: colors.primaryText }}>
                {formatPrice(charges)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span style={{ color: colors.secondaryText }}>Available Balance</span>
              <span
                className="font-semibold"
                style={{
                  color: canAfford ? colors.profit : colors.loss,
                }}
              >
                {formatPrice(user?.virtual_balance || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Swipe to Execute */}
        <div
          className="app-card fixed left-0 right-0 p-3 z-40"
          style={{
            background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`,
            borderTop: `1px solid ${colors.border}`,
            bottom: '64px', // Above the bottom navigation bar
          }}
        >
          <SwipeToExecute
            orderType={orderType}
            onExecute={handleSwipeExecute}
            disabled={!canAfford}
          />
        </div>
      </div>

      {/* Modals */}
      <OrderConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmOrder}
        orderDetails={pendingOrder}
      />

      <TradeJournalModal
        isOpen={showJournal}
        onClose={() => setShowJournal(false)}
        onSubmit={handleJournalSubmit}
        orderDetails={pendingOrder}
      />
    </>
  );
}

export default AdvancedTradePage;
